import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Shield, UserPlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, IconChip } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  ApiError,
  createRelationship,
  getRelationships,
  respondRelationship,
  revokeRelationship,
  setShareLevel,
  type IncomingRequest,
  type OutgoingShare,
  type RelationshipRole,
  type ShareLevel,
} from "@/lib/api";

const ROLE_LABEL: Record<string, string> = {
  sponsor: "Sponsor",
  trusted_contact: "Trusted contact",
};
const SHARE_LABEL: Record<ShareLevel, string> = {
  none: "Paused",
  individual: "Status only",
  selected_history: "Status only",
  full_history: "Full history",
};
const STATUS_BADGE: Record<string, "green" | "yellow" | "red"> = {
  active: "green",
  pending: "yellow",
  revoked: "red",
};

export default function Sharing() {
  const navigate = useNavigate();
  const [outgoing, setOutgoing] = useState<OutgoingShare[]>([]);
  const [incoming, setIncoming] = useState<IncomingRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<RelationshipRole>("sponsor");
  const [level, setLevel] = useState<ShareLevel>("individual");

  async function load() {
    try {
      const r = await getRelationships();
      setOutgoing(r.outgoing);
      setIncoming(r.incoming.filter((i) => i.status === "pending"));
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) navigate("/signin");
      else setError("Couldn't load your sharing settings.");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function invite() {
    if (!email.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await createRelationship({ target_email: email.trim(), role, share_level: level });
      setEmail("");
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't send the invite.");
    } finally {
      setBusy(false);
    }
  }

  async function changeLevel(id: string, share_level: ShareLevel) {
    await setShareLevel(id, share_level).catch(() => setError("Couldn't update."));
    await load();
  }
  async function respond(id: string, accept: boolean) {
    await respondRelationship(id, accept).catch(() => setError("Couldn't respond."));
    await load();
  }
  async function remove(id: string) {
    await revokeRelationship(id).catch(() => setError("Couldn't revoke."));
    await load();
  }

  if (loading) {
    return (
      <div className="flex min-h-dvh items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" strokeWidth={2} />
        <span className="text-sm">Loading…</span>
      </div>
    );
  }

  const activeShares = outgoing.filter((r) => r.status !== "revoked");

  return (
    <div className="pb-16">
      <div className="flex items-center gap-2 px-4 pb-2 pt-6">
        <Button variant="ghost" size="icon" aria-label="Back" onClick={() => navigate("/app")}>
          <ArrowLeft className="h-5 w-5" strokeWidth={2} />
        </Button>
        <h1 className="flex-1 text-lg font-semibold text-foreground">Sharing</h1>
      </div>

      <div className="flex flex-col gap-4 px-6 pt-2">
        {error && <p className="text-sm text-status-red-text">{error}</p>}

        <Card>
          <CardContent className="flex items-start gap-3 p-4">
            <IconChip icon={Shield} tint="primary" />
            <p className="text-[13px] leading-relaxed text-muted-foreground">
              You decide who sees what. Trusted contacts receive a quiet email if you
              log a Red day. Sponsors can view what you share once they accept. Your
              journal stays yours.
            </p>
          </CardContent>
        </Card>

        {/* Incoming sponsor requests */}
        {incoming.length > 0 && (
          <section className="flex flex-col gap-2">
            <h2 className="text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">
              Requests for you
            </h2>
            {incoming.map((r) => (
              <Card key={r.id}>
                <CardContent className="flex flex-col gap-3 px-4 py-3">
                  <p className="text-sm text-foreground">
                    <span className="font-medium">{r.requester_name}</span> asked you to be
                    their {ROLE_LABEL[r.role]?.toLowerCase() ?? "contact"}.
                  </p>
                  <div className="flex gap-2">
                    <Button onClick={() => respond(r.id, true)}>Accept</Button>
                    <Button variant="ghost" onClick={() => respond(r.id, false)}>
                      Decline
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </section>
        )}

        {/* Invite */}
        <section className="flex flex-col gap-2">
          <h2 className="text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">
            Invite someone
          </h2>
          <Card>
            <CardContent className="flex flex-col gap-3 p-4">
              <Input
                type="email"
                placeholder="Their email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <div>
                <p className="mb-1.5 text-[13px] text-muted-foreground">They are my…</p>
                <div className="flex gap-2">
                  {(["sponsor", "trusted_contact"] as RelationshipRole[]).map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setRole(r)}
                      className={`flex-1 rounded-md border-2 py-2 text-sm font-medium transition-colors ${
                        role === r
                          ? "border-primary bg-primary/[0.06] text-foreground"
                          : "border-border text-muted-foreground"
                      }`}
                    >
                      {ROLE_LABEL[r]}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-1.5 text-[13px] text-muted-foreground">They can see…</p>
                <div className="flex gap-2">
                  {(
                    [
                      ["individual", "Status only"],
                      ["full_history", "Full history"],
                    ] as [ShareLevel, string][]
                  ).map(([lv, lbl]) => (
                    <button
                      key={lv}
                      type="button"
                      onClick={() => setLevel(lv)}
                      className={`flex-1 rounded-md border-2 py-2 text-sm font-medium transition-colors ${
                        level === lv
                          ? "border-primary bg-primary/[0.06] text-foreground"
                          : "border-border text-muted-foreground"
                      }`}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
              <Button disabled={busy || !email.trim()} onClick={invite}>
                {busy ? (
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
                ) : (
                  <>
                    <UserPlus className="h-4 w-4" strokeWidth={2} />
                    Send invite
                  </>
                )}
              </Button>
              <p className="text-xs leading-relaxed text-muted-foreground">
                A trusted contact starts receiving Red-day alerts right away. A sponsor
                sees nothing until they accept.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* People I share with */}
        <section className="flex flex-col gap-2">
          <h2 className="text-xs font-semibold uppercase tracking-[0.05em] text-muted-foreground">
            People you share with
          </h2>
          {activeShares.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No one yet. Invite a sponsor or trusted contact above.
            </p>
          ) : (
            activeShares.map((r) => (
              <Card key={r.id}>
                <CardContent className="flex flex-col gap-3 px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">
                        {r.target_email}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {ROLE_LABEL[r.role] ?? r.role}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge status={STATUS_BADGE[r.status]}>
                        {r.status === "active"
                          ? "Active"
                          : r.status === "pending"
                            ? "Pending"
                            : "Ended"}
                      </Badge>
                      <button
                        type="button"
                        aria-label="Revoke"
                        className="text-muted-foreground"
                        onClick={() => remove(r.id)}
                      >
                        <X className="h-4 w-4" strokeWidth={2} />
                      </button>
                    </div>
                  </div>
                  {/* Share-level toggle */}
                  <div className="flex gap-2">
                    {(
                      [
                        ["individual", "Status only"],
                        ["full_history", "Full history"],
                        ["none", "Paused"],
                      ] as [ShareLevel, string][]
                    ).map(([lv, lbl]) => {
                      const on =
                        (lv === "individual" &&
                          (r.share_level === "individual" || r.share_level === "selected_history")) ||
                        r.share_level === lv;
                      return (
                        <button
                          key={lv}
                          type="button"
                          onClick={() => changeLevel(r.id, lv)}
                          className={`flex-1 rounded-md border py-1.5 text-xs font-medium transition-colors ${
                            on
                              ? "border-primary bg-primary/[0.06] text-foreground"
                              : "border-border text-muted-foreground"
                          }`}
                        >
                          {lbl}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    {r.role === "trusted_contact"
                      ? "Receives Red-day alert emails."
                      : r.status === "active"
                        ? `Can view your ${SHARE_LABEL[r.share_level].toLowerCase()}.`
                        : "Waiting for them to accept."}
                  </p>
                </CardContent>
              </Card>
            ))
          )}
        </section>

        <p className="text-center text-xs text-muted-foreground">
          Sharing is consented on both sides. Either of you can end it anytime.
        </p>
      </div>
    </div>
  );
}
